'use client';

import { useEffect, useState, useCallback } from 'react';
import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { useRouter, usePathname } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { ForbiddenIllustration } from 'src/assets/illustrations';
import { varBounce, MotionContainer } from 'src/components/animate';
import { SplashScreen } from 'src/components/loading-screen';

import { useAuthContext } from '../hooks';
import { hasRouteAccess, getDefaultRedirect } from '../rbac';

/**
 * RBAC Guard Component
 *
 * Protects routes based on user role and route permissions.
 * Redirects unauthorized users to their default dashboard.
 */
export function RBACGuard({ children }) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, loading, authenticated } = useAuthContext();

  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  const checkAccess = useCallback(() => {
    // Still loading auth state
    if (loading) {
      return;
    }

    // Not authenticated - AuthGuard will handle redirect to login
    if (!authenticated || !user) {
      setIsChecking(false);
      setHasAccess(true); // Let AuthGuard handle unauthenticated users
      return;
    }

    const userRole = user.role?.toUpperCase();
    const canAccess = hasRouteAccess(pathname, userRole);

    setHasAccess(canAccess);
    setIsChecking(false);
  }, [loading, authenticated, user, pathname]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  // Show loading screen while checking
  if (isChecking || loading) {
    return <SplashScreen />;
  }

  // If user doesn't have access, show forbidden page
  if (!hasAccess && authenticated) {
    const userRole = user?.role?.toUpperCase();
    const redirectPath = getDefaultRedirect(userRole);

    return (
      <Container component={MotionContainer} sx={{ textAlign: 'center', py: 10 }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            Permission Denied
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>
            You do not have permission to access this page.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your role: <strong>{userRole || 'Unknown'}</strong>
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <ForbiddenIllustration sx={{ my: { xs: 5, sm: 8 }, height: 260 }} />
        </m.div>

        <m.div variants={varBounce().in}>
          <Button
            variant="contained"
            size="large"
            onClick={() => router.push(redirectPath)}
            sx={{
              background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2E7D32 0%, #388E3C 100%)',
              },
            }}
          >
            Go to Dashboard
          </Button>
        </m.div>
      </Container>
    );
  }

  return <>{children}</>;
}
